import { TFunction } from 'react-i18next';
import * as Yup from 'yup';

import {
  DISTRICT_ASYNC_SELECT,
  DOCUMENT_UPLOAD,
  STATE_AND_DISTRICT,
  STATE_ASYNC_SELECT
} from '@nw/shared/config';

import {
  EMAIL_REGEX_TYPE,
  emailIdRegularExpression,
  INVITE_CODE_REGEX_TYPE,
  inviteCodeRegularExpression,
  NUMBER_REGEX_TYPE,
  numberRegularExpression,
  otpRegularExpression,
  PAN_CARD_REGEX_TYPE,
  panCardRegularExpression,
  PHONE_NUMBER_REGEX_TYPE,
  phoneNumberRegularExpression,
  PINCODE_REGEX_TYPE,
  pincodeRegularExpression,
  YEAR_REGEX_TYPE,
  yearRegularExpression
} from '../../constants/ValidationRegex';

export const otpValidationSchema = Yup.string().matches(otpRegularExpression);

export const phoneNumberValidationSchema = Yup.string().matches(
  phoneNumberRegularExpression
);

export const emailValidationSchema = Yup.string().matches(
  emailIdRegularExpression
);

export const isValidSixDigitOTP = (value: string): boolean =>
  otpValidationSchema.isValidSync(value);

export const isValidPhoneNumber = (value: string): boolean =>
  phoneNumberValidationSchema.isValidSync(value);

export const isValidEmail = (value: string): boolean =>
  emailValidationSchema.isValidSync(value);

const createYupSchema = (t: TFunction) => (schema, config) => {
  const {
    field,
    supportingField,
    validationType,
    type,
    validations = []
  } = config;
  if (!Yup[validationType]) {
    return schema;
  }
  let validator = Yup[validationType]();
  validations.forEach((validation) => {
    const { params, type } = validation;
    if (!validator[type]) {
      return;
    }

    validator = validator[type](...params);
  });
  if (field.indexOf('.') !== -1) {
    // nested fields are not covered
  } else {
    if (type === STATE_AND_DISTRICT) {
      schema[`${field}State`] = validator;
      schema[`${field}District`] = validator;
    } else if (type === DOCUMENT_UPLOAD) {
      schema[field] = validator;
      schema[supportingField] = validator;
    } else if (type === STATE_ASYNC_SELECT) {
      schema[field] = Yup.object()
        .shape({
          country_id: Yup.string().required(t('validationMessages.required')),
          state_id: Yup.string().required(t('validationMessages.required')),
          state_name: Yup.string().required(t('validationMessages.required'))
        })
        .required(t('validationMessages.required'))
        .nullable();
    } else if (type === DISTRICT_ASYNC_SELECT) {
      schema[field] = Yup.object()
        .shape({
          state_id: Yup.string().required(t('validationMessages.required')),
          district_id: Yup.string().required(t('validationMessages.required')),
          district_name: Yup.string().required(t('validationMessages.required'))
        })
        .required(t('validationMessages.required'))
        .nullable();
    } else schema[field] = validator;
  }
  return schema;
};

export const checkIsFieldPresent = (fields, field) => {
  return fields.find((eachField) => field === eachField.field);
};

const getRegexSchema = (regexType, validationMessage, t: TFunction) => {
  switch (regexType) {
    case PHONE_NUMBER_REGEX_TYPE:
      return Yup.string()
        .required(t('validationMessages.required'))
        .matches(phoneNumberRegularExpression, validationMessage);
    case EMAIL_REGEX_TYPE:
      return Yup.string()
        .required(t('validationMessages.required'))
        .matches(emailIdRegularExpression, validationMessage);
    case PINCODE_REGEX_TYPE:
      return Yup.string()
        .required(t('validationMessages.required'))
        .matches(pincodeRegularExpression, validationMessage);
    case INVITE_CODE_REGEX_TYPE:
      return Yup.string()
        .transform((value, originalValue) => {
          if (value === '') return null;
          return originalValue;
        })
        .required(t('validationMessages.required'))
        .matches(inviteCodeRegularExpression, validationMessage)
        .nullable();
    case YEAR_REGEX_TYPE:
      return Yup.string()
        .required(t('validationMessages.required'))
        .matches(yearRegularExpression, 'Enter valid year');
    case PAN_CARD_REGEX_TYPE:
      return Yup.string()
        .required(t('validationMessages.required'))
        .matches(panCardRegularExpression, validationMessage);
    case NUMBER_REGEX_TYPE:
      return Yup.string()
        .required(t('validationMessages.required'))
        .matches(
          numberRegularExpression,
          validationMessage ?? 'Enter valid Number'
        );
    default: {
      return '';
    }
  }
};

//FIXME:Give type to formToBeDisplayed
const RegistrationFormYupValidationSchema = (
  formToBeDisplayed,
  t: TFunction
): Yup.AnyObjectSchema => {
  let yupValidationSchema = {};
  const selectFieldSchema: any = {};
  const checkboxSchema: any = {};
  const regexSchema: any = {};

  if (formToBeDisplayed.allFormFields) {
    yupValidationSchema = formToBeDisplayed.allFormFields.reduce(
      createYupSchema(t),
      {}
    );

    formToBeDisplayed.allFormFields.forEach((eachField: any) => {
      const {
        type,
        validations,
        validationType,
        regexType,
        validationMessage,
        field
      } = eachField;
      if (type === 'checkbox' && validationType) {
        checkboxSchema[eachField.field] = Yup.bool().oneOf(
          [true],
          validations[0]
            ? validations[0].error_message
              ? validations[0].error_message
              : t('validationMessages.required')
            : t('validationMessages.required')
        );
      }
      if (regexType && validationType) {
        regexSchema[field] = getRegexSchema(regexType, validationMessage, t);
      }
    });
  }
  const isSelectedOccupationPresent = formToBeDisplayed.allFormFields.find(
    (eachField: any) => eachField.field === 'selectedOccupation'
  )
    ? true
    : false;

  const validationSchema = Yup.object().shape({
    program: Yup.string().required(t('validationMessages.required')),
    inviteCode: Yup.string()
      .transform((value, originalValue) => {
        if (value === '') return null;
        return originalValue;
      })
      .matches(
        inviteCodeRegularExpression,
        t('validationMessages.pleaseEnterAValidInviteCode')
      )
      .nullable(),
    fullName: checkIsFieldPresent(formToBeDisplayed.allFormFields, 'fullName')
      ? Yup.string().required(t('validationMessages.required'))
      : Yup.string().nullable(),
    email: Yup.string()
      .trim(t('validationMessages.required'))
      .required(t('validationMessages.required'))
      .matches(
        emailIdRegularExpression,
        t('validationMessages.notAValidEmail')
      ),
    phone: Yup.string()
      .matches(
        phoneNumberRegularExpression,
        t('validationMessages.pleaseEnterAValidPhoneNumber')
      )
      .required(t('validationMessages.required')),
    selectedOccupation: isSelectedOccupationPresent
      ? Yup.string().required(t('validationMessages.required'))
      : Yup.string(),
    selectedGender: checkIsFieldPresent(
      formToBeDisplayed.allFormFields,
      'selectedGender'
    )
      ? Yup.string().required(t('validationMessages.required'))
      : Yup.string().nullable(),
    currentState: Yup.string().required(t('validationMessages.required')),
    currentDistrict: checkIsFieldPresent(
      formToBeDisplayed.allFormFields,
      'stateAndDistrict'
    )
      ? Yup.string().required(t('validationMessages.required'))
      : Yup.string(),
    currentCity: checkIsFieldPresent(
      formToBeDisplayed.allFormFields,
      'currentCity'
    )
      ? Yup.string().required(t('validationMessages.required'))
      : Yup.string(),
    whatsappInfoStatus: Yup.bool().oneOf(
      [true, false],
      t('validationMessages.required')
    ),
    receiveUpdatesInfoStatus: Yup.bool().oneOf(
      [true, false],
      t('validationMessages.required')
    ),
    isUserHasPan: Yup.string().required(t('validationMessages.required')),
    nameAsPerThePanCard: Yup.string()
      .trim(t('validationMessages.required'))
      .required(t('validationMessages.required')),
    panCardNumber: Yup.string()
      .matches(
        panCardRegularExpression,
        t('validationMessages.enterAValidPanCardNumber')
      )
      .required(t('validationMessages.required')),
    fathersNameAsPerYourPanCard: Yup.string()
      .trim(t('validationMessages.required'))
      .required(t('validationMessages.required')),
    dobAsPerPanCard: Yup.string()
      .trim(t('validationMessages.required'))
      .required(t('validationMessages.required')),
    panCardSelfAttestedFileUrl: Yup.string()
      .trim(t('validationMessages.required'))
      .required(t('validationMessages.required')),
    ...yupValidationSchema,
    ...selectFieldSchema,
    ...checkboxSchema,
    ...regexSchema
  });

  return validationSchema;
};

export default RegistrationFormYupValidationSchema;
